import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as echarts from 'echarts';
import ReactEcharts from 'echarts-for-react';

import BZAPIService from '../../../services/BZAPIService';
import Loader from '../../common/Loader';
import DeliveriesTable from './DeliveriesTable';
import { sentChartOption, openChartOption, openChartHeight } from './charts';

// The endpoint rejects days=0, so there is no "All time" option.
const RANGES = [7, 30, 90, 365];
const VENDOR_PAGE = 20;

const StatTile = ({ label, value, meta, accent }) => (
  <div className="bz-stat" style={accent ? { borderTop: `3px solid ${accent}` } : undefined}>
    <div className="bz-stat__label">{label}</div>
    <div className="bz-stat__value bz-tnum">{value}</div>
    {meta && <div className="bz-stat__meta">{meta}</div>}
  </div>
);

StatTile.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node,
  meta: PropTypes.node,
  accent: PropTypes.string
};

// The tile is a quarter of the row, so a full instance URL would blow the
// layout out. Show the host, link the whole thing, keep the full URL in the
// tooltip. `color` carries the delivery-health status; the size overrides the
// --fs-xs the surrounding bz-stat__meta imposes, which is too small to read a
// hostname comfortably.
const InstanceLink = ({ url, color }) => {
  if (!url) return null;
  let host = url;
  try {
    host = new URL(url).host;
  } catch {
    // Not a parseable URL (e.g. a half-typed setting) — fall back to the raw
    // string rather than hiding the value.
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={url}
      className="bz-truncate"
      style={{
        color: color || 'var(--info)',
        fontSize: 'var(--fs-lg)',
        textDecoration: 'none',
        display: 'block'
      }}
    >
      {host}
    </a>
  );
};

InstanceLink.propTypes = { url: PropTypes.string, color: PropTypes.string };

const SectionCard = ({ title, sub, right, children, footer }) => (
  <div className="bz-card">
    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--line-soft)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <h3 className="bz-h3">{title}</h3>
        <div>{right}</div>
      </div>
      {sub && <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-muted)', marginTop: 3 }}>{sub}</div>}
    </div>
    {children}
    {footer}
  </div>
);

SectionCard.propTypes = {
  title: PropTypes.string,
  sub: PropTypes.node,
  right: PropTypes.node,
  children: PropTypes.node,
  footer: PropTypes.node
};

const EmptyBlock = ({ title, children }) => (
  <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--ink-muted)', fontSize: 'var(--fs-md)' }}>
    {title && <div style={{ fontWeight: 600, color: 'var(--ink)', marginBottom: 6 }}>{title}</div>}
    {children}
  </div>
);

EmptyBlock.propTypes = { title: PropTypes.string, children: PropTypes.node };

const fmt = n => (n === null || n === undefined ? '—' : n.toLocaleString());

const SnStatsTab = ({ snApiUrl }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Deliberately NOT reset when `days` changes: open tickets are a live
  // snapshot and are unaffected by the range, so collapsing would look broken.
  const [showAllVendors, setShowAllVendors] = useState(false);

  // Bumped on every fetch so only the newest one may write state. Without this,
  // clicking 7d then 30d applies whichever response lands last — you can end up
  // reading 7 days of data under a "last 30 days" label.
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    const isStale = () => seq !== requestSeq.current;

    setIsLoading(true);
    setError(null);
    try {
      const payload = await BZAPIService.getServiceNowStats({ days });
      if (isStale()) return;
      setData(payload);
    } catch (e) {
      if (isStale()) return;
      console.log('getServiceNowStats failed', e);
      setError(
        'The request did not complete. This can happen if ServiceNow settings are missing or the service is temporarily unavailable.'
      );
    } finally {
      if (!isStale()) setIsLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
    // Invalidate the in-flight request on unmount so a late response can't
    // write to an unmounted tree.
    return () => {
      requestSeq.current += 1;
    };
  }, [load]);

  const open = data?.open_tickets_in_sn;

  const vendorsDesc = useMemo(
    () => (open?.by_company ? open.by_company.slice().sort((a, b) => b.count - a.count) : []),
    [open]
  );
  const vendorsShown = showAllVendors ? vendorsDesc : vendorsDesc.slice(0, VENDOR_PAGE);
  // ECharts renders category index 0 at the bottom, so reverse for display.
  const vendorsAsc = vendorsShown.slice().reverse();
  const hiddenTicketCount = vendorsDesc.slice(VENDOR_PAGE).reduce((s, r) => s + r.count, 0);

  // A window whose every day is 0/0 is real data, but plotting a flat line at
  // zero says less than saying so in words — and it's the normal state before
  // ServiceNow is connected.
  const sentRows = data?.tickets_sent_by_date || [];
  const hasSentActivity = sentRows.some(r => r.created > 0 || r.updated > 0);

  const deliveries = data?.bep_executions || [];
  // Only genuine failures count as error days. A run that couldn't start because
  // ServiceNow isn't connected yet is a setup state — reporting it as an error
  // tells the customer something is broken when nothing is.
  const errorDays = deliveries.filter(r => r.error && r.errorKind !== 'not_configured').length;
  const notConnectedDays = deliveries.filter(r => r.errorKind === 'not_configured').length;

  // Treat the integration as unconfigured if either side says so: the live
  // ServiceNow call, or any delivery run that bailed for that reason.
  const notConnected = open?.errorKind === 'not_configured' || notConnectedDays > 0;

  // `linkColor` tints the instance URL on this tile to match delivery health, so
  // the link reads as part of the status rather than as generic chrome.
  // --ok is #2E7D5B and --bz-orange-1 is #E1523D — the requested colours exactly,
  // so no raw hex. --bz-orange-1 rather than --prevent: the value is identical,
  // but --prevent is the product accent and shouldn't be borrowed for a status.
  const healthTile = errorDays
    ? {
        value: `${errorDays} error day${errorDays > 1 ? 's' : ''}`,
        accent: 'var(--bad)',
        linkColor: 'var(--bz-orange-1)'
      }
    : notConnected
    ? { value: 'Not connected', accent: 'var(--warn)', linkColor: 'var(--warn)' }
    : { value: 'Healthy', accent: 'var(--ok)', linkColor: 'var(--ok)' };

  if (isLoading && !data) return <Loader text="Loading ServiceNow stats..." />;

  if (error) {
    return (
      <div className="bz-card">
        <EmptyBlock title="Could not load ServiceNow stats">
          <div>{error}</div>
          <div style={{ marginTop: 12 }}>
            <button type="button" className="bz-btn bz-btn--sm" onClick={load}>
              Try again
            </button>
          </div>
        </EmptyBlock>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {notConnected && (
        <div
          data-testid="sn-stats-not-connected"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '12px 14px',
            background: 'var(--warn-soft)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-sm)',
            fontSize: 'var(--fs-sm)',
            color: 'var(--ink)'
          }}
        >
          <span className="bz-pill bz-pill--warn" style={{ flexShrink: 0 }}>
            Setup
          </span>
          <div>
            <strong>ServiceNow isn&apos;t connected yet.</strong>{' '}
            {open?.errorKind === 'not_configured' && open?.error ? `${open.error}. ` : ''}
            These figures stay at zero until the connection is working — nothing here is broken. Add the instance URL
            and credentials on the <strong>Credentials</strong> tab to start sending bugs to ServiceNow.
          </div>
        </div>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap'
        }}
      >
        <div>
          <div className="bz-kicker">Ticketing pipeline</div>
          <div className="bz-muted" style={{ fontSize: 'var(--fs-sm)', marginTop: 4 }}>
            {data?.window
              ? `Last ${days} days · ${data.window.from.slice(0, 10)} → ${data.window.to.slice(0, 10)} (${
                  data.window.tz
                })`
              : `Last ${days} days`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div
            style={{
              display: 'inline-flex',
              border: '1px solid var(--line)',
              borderRadius: 'var(--r-md)',
              overflow: 'hidden'
            }}
          >
            {RANGES.map(d => (
              <button
                key={d}
                type="button"
                data-testid={`sn-stats-range-${d}`}
                aria-pressed={days === d}
                onClick={() => setDays(d)}
                style={{
                  all: 'unset',
                  cursor: 'pointer',
                  padding: '6px 14px',
                  fontSize: 'var(--fs-md)',
                  fontWeight: days === d ? 600 : 500,
                  color: days === d ? 'var(--ink-invert)' : 'var(--ink-soft)',
                  background: days === d ? 'var(--bz-blue-1)' : 'var(--bg-elev)',
                  borderRight: '1px solid var(--line)'
                }}
              >
                {d === 365 ? '1y' : `${d}d`}
              </button>
            ))}
          </div>
          <button type="button" className="bz-btn bz-btn--sm" onClick={load} disabled={isLoading}>
            Refresh
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
        <StatTile
          label="Tickets created"
          value={fmt(data?.tickets_sent_all_time?.created)}
          meta="All time"
          accent="var(--prevent)"
        />
        <StatTile
          label="Tickets updated"
          value={fmt(data?.tickets_sent_all_time?.updated)}
          meta="All time"
          accent="var(--bz-blue-2)"
        />
        <StatTile
          label="Open in ServiceNow"
          value={open?.error ? '—' : fmt(open?.total)}
          meta={open?.error ? 'Unavailable' : 'Open now, all vendors'}
          accent="var(--plan)"
        />
        <StatTile
          label="Delivery health"
          value={healthTile.value}
          // The instance replaces the run count here — the per-day run counts
          // are already a column in the deliveries table below, so nothing is
          // lost, and knowing *which* ServiceNow this is matters more.
          meta={<InstanceLink url={snApiUrl} color={healthTile.linkColor} />}
          accent={healthTile.accent}
        />
      </div>

      <SectionCard
        title="Tickets sent to ServiceNow"
        sub="Tickets BugZero created and updated per day."
        right={<span className="bz-pill">{data?.window?.tz || 'UTC'}</span>}
      >
        {hasSentActivity ? (
          <div style={{ padding: '12px 8px 4px' }}>
            <ReactEcharts
              echarts={echarts}
              option={sentChartOption(sentRows)}
              style={{ height: 260, width: '100%' }}
              notMerge
            />
          </div>
        ) : (
          <EmptyBlock>No tickets sent in this window.</EmptyBlock>
        )}
      </SectionCard>

      <SectionCard
        title="Open tickets in ServiceNow by vendor"
        sub="Open now — grouped by the ServiceNow company mapped to each vendor (its Vendor System ID). Not affected by the range above."
        right={
          open?.error ? (
            <span className={`bz-pill ${open.errorKind === 'not_configured' ? 'bz-pill--warn' : 'bz-pill--bad'}`}>
              <span className="bz-dot" />
              {open.errorKind === 'not_configured' ? 'Not connected' : 'Unavailable'}
            </span>
          ) : (
            <span className="bz-pill">{`Total ${fmt(open?.total)}`}</span>
          )
        }
        footer={
          !open?.error && vendorsDesc.length > VENDOR_PAGE ? (
            <div
              style={{
                padding: '10px 16px',
                borderTop: '1px solid var(--line-soft)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10
              }}
            >
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-muted)' }}>
                {`Showing top ${vendorsShown.length} of ${vendorsDesc.length} vendors`}
                {!showAllVendors && ` · ${hiddenTicketCount.toLocaleString()} tickets in the rest`}
              </span>
              <button type="button" className="bz-btn bz-btn--sm" onClick={() => setShowAllVendors(!showAllVendors)}>
                {showAllVendors ? 'Show less' : 'Show more'}
              </button>
            </div>
          ) : null
        }
      >
        {open?.error ? (
          <EmptyBlock
            title={
              open.errorKind === 'not_configured' ? 'Waiting on the ServiceNow connection' : "Couldn't reach ServiceNow"
            }
          >
            <div>{open.error}</div>
            <div style={{ marginTop: 10 }}>
              <span className="bz-pill bz-pill--warn">Delivery history below is unaffected</span>
            </div>
          </EmptyBlock>
        ) : vendorsAsc.length ? (
          <div style={{ padding: '12px 8px' }}>
            <ReactEcharts
              echarts={echarts}
              option={openChartOption(vendorsAsc)}
              style={{ height: openChartHeight(vendorsAsc.length), width: '100%' }}
              notMerge
            />
          </div>
        ) : (
          <EmptyBlock>No open BugZero tickets in ServiceNow.</EmptyBlock>
        )}
      </SectionCard>

      <SectionCard
        title="ServiceNow bug deliveries"
        sub="Daily record of BugZero sending bugs into ServiceNow."
        right={
          errorDays ? (
            <span className="bz-pill bz-pill--bad">
              <span className="bz-dot" />
              {`${errorDays} error day${errorDays > 1 ? 's' : ''}`}
            </span>
          ) : null
        }
      >
        <DeliveriesTable rows={deliveries} />
      </SectionCard>
    </div>
  );
};

SnStatsTab.propTypes = {
  // Comes from the settings the page already loaded, so showing it costs no
  // extra request. The stats endpoint doesn't return it.
  snApiUrl: PropTypes.string
};

export default SnStatsTab;
